const normalizeMavieproSurveysList = (items, domainUuid) => {
  const ensureArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : [])

  return (Array.isArray(items) ? items : [])
    .map(item => {
      const surveysArray = Array.isArray(item?.surveys) ? item.surveys : []
      const firstSurvey = surveysArray.length ? surveysArray[0] : null

      const updatedAt = firstSurvey?.updated_at || item?.updated_at || null
      const title = item?.group_title || firstSurvey?.question || 'Sondage Maviepro'
      const uuid = item?.uuid || firstSurvey?.uuid || null
      const isEnabled = typeof item?.is_enabled === 'boolean'
        ? item.is_enabled
        : (typeof firstSurvey?.is_enabled === 'boolean' ? firstSurvey.is_enabled : false)

      const duplicatedBySource = item?.duplicated_by ?? firstSurvey?.duplicated_by ?? []
      const duplicatedBy = ensureArray(duplicatedBySource)

      return {
        uuid,
        title,
        updatedAt,
        isEnabled,
        duplicatedBy
      }
    })
    .filter(entry => {
      if (!entry || entry.isEnabled) {
        return false
      }
      if (!entry.updatedAt || !entry.uuid) {
        return false
      }

      const updatedAtDate = new Date(entry.updatedAt)
      if (Number.isNaN(updatedAtDate.getTime())) {
        return false
      }

      if (domainUuid && entry.duplicatedBy && entry.duplicatedBy.includes(domainUuid)) {
        return false
      }

      return true
    })
    .sort((a, b) => {
      const dateA = new Date(a.updatedAt)
      const dateB = new Date(b.updatedAt)
      return dateB.getTime() - dateA.getTime()
    })
}

const toPositiveNumber = (value) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null
}

const hasManualDomainCapacity = (domain) => Boolean(domain?.has_manual_employees_capacity ?? domain?.hasManualEmployeesCapacity)

const getDomainDisplayCapacity = (domain) => {
  if (!domain) {
    return null
  }

  const effective = toPositiveNumber(domain?.effective_employees_capacity ?? domain?.effectiveEmployeesCapacity)
  if (effective !== null) {
    return effective
  }

  const manual = toPositiveNumber(domain?.employees_capacity ?? domain?.employeesCapacity)
  if (manual !== null) {
    return manual
  }

  return null
}

export default {
  dashboardChartStartDate () {
    const selectedStart = String(this.dateStart || '').slice(0, 10)
    const launchDate = String(this.domainStartDate || this.domain?.launch_date || '').slice(0, 10)
    const validDate = /^\d{4}-\d{2}-\d{2}$/
    const dates = [selectedStart, launchDate].filter(value => validDate.test(value))

    return dates.length ? dates.sort().pop() : null
  },
  dashboardEmployeeActivityDailySeries () {
    const rows = Array.isArray(this.employeeActivityDailySeries) ? this.employeeActivityDailySeries : []
    if (!this.dashboardChartStartDate) {
      return rows
    }

    return rows.filter(row => String(row?.date || '').slice(0, 10) >= this.dashboardChartStartDate)
  },
  dashboardReadTrackingDailySeries () {
    const rows = Array.isArray(this.readTrackingDailySeries) ? this.readTrackingDailySeries : []
    if (!this.dashboardChartStartDate) {
      return rows
    }

    return rows.filter(row => String(row?.date || '').slice(0, 10) >= this.dashboardChartStartDate)
  },
  userGreeting () {
    return this.user?.firstname || this.user?.email || ''
  },
  userStatusLabel () {
    if (this.roles && this.roles.length) {
      const labels = {
        admin: 'Administrateur',
        director: 'Directeur',
        'community-manager': 'Community manager',
        operator: 'Opérateur'
      }
      const primary = this.roles[0]
      return labels[primary] || primary
    }
    return this.user?.email || 'Utilisateur connecté'
  },
  hasNewsEditionRights () {
    return Array.isArray(this.roles) && this.roles.some(role => ['admin', 'director', 'community-manager'].includes(role))
  },
  hasSurveyEditionRights () {
    return Array.isArray(this.roles) && this.roles.some(role => ['admin', 'director', 'community-manager'].includes(role))
  },
  topNews () {
    return Array.isArray(this.topNewsItems) ? this.topNewsItems.slice(0, 5) : []
  },
  topFaqs () {
    return (this.popularity?.faqs || []).slice(0, 5)
  },
  topSearches () {
    return (this.popularity?.searches || []).slice(0, 10)
  },
  totalSearches () {
    const periodTotal = Number(this.popularity?.totals?.searches?.period)
    if (Number.isFinite(periodTotal) && periodTotal >= 0) {
      return periodTotal
    }

    return (this.popularity?.searches || []).reduce((total, item) => total + (item.total || 0), 0)
  },
  keywordAlerts () {
    const sensitivities = this.alertKeywordsReference.map(word => ({
      word,
      normalized: this.normalizeKeyword(word)
    }))
    const counts = {}
    ;(this.popularity?.searches || []).forEach(search => {
      const normalizedQuery = this.normalizeKeyword(search.query)
      sensitivities.forEach(entry => {
        if (normalizedQuery.includes(entry.normalized)) {
          counts[entry.word] = (counts[entry.word] || 0) + (search.total || 0)
        }
      })
    })
    return Object.entries(counts)
      .map(([word, total]) => ({ word, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 3)
  },
  employeeBase () {
    const { manualOverride, totalEmployees } = this.mockEmployeeTargets || {}
    const override = Number(manualOverride)
    if (Number.isFinite(override) && override > 0) {
      return override
    }

    const domainEmployees = Number(this.domain?.stats?.employees || this.domain?.employees_count || this.domain?.employeesCount)
    if (Number.isFinite(domainEmployees) && domainEmployees > 0) {
      return domainEmployees
    }

    if (totalEmployees === null || totalEmployees === undefined) {
      return null
    }

    const fallback = Number(totalEmployees)
    if (Number.isFinite(fallback) && fallback > 0) {
      return fallback
    }

    return 0
  },
  employeeBaseDisplay () {
    const displayed = getDomainDisplayCapacity(this.domain)
    if (displayed !== null) {
      return displayed
    }
    return this.employeeBase
  },
  downloadCompletionRate () {
    const base = this.employeeBaseDisplay || this.employeeBase
    if (!base) {
      return 0
    }
    const ratio = (this.counters?.total?.users || 0) / base
    return Math.max(0, Math.min(100, Math.round(ratio * 100)))
  },
  downloadGaugeStyle () {
    const progress = this.downloadCompletionRate
    const angle = (progress / 100) * 360
    return {
      background: `conic-gradient(#2D5DFB ${angle}deg, #E2E8FE ${angle}deg)`
    }
  },
  downloadGaugeSourceLabel () {
    if (hasManualDomainCapacity(this.domain)) {
      return 'effectif renseigné'
    }
    if (this.mockEmployeeTargets.manualOverride) {
      return 'effectif renseigné'
    }
    if (Number(this.mockEmployeeTargets.totalEmployees || 0) > 0) {
      return 'effectif estimé'
    }
    return 'base utilisateurs'
  },
  uniqueConnectionsCount () {
    if (Array.isArray(this.loginsSeries) && this.loginsSeries.length) {
      return this.loginsSeries.reduce((total, point) => {
        const value = Number(point?.unique ?? 0)
        return total + (Number.isFinite(value) ? value : 0)
      }, 0)
    }

    if (this.graphs && typeof this.graphs === 'object') {
      const dailyValues = Object.values(this.graphs)
      if (dailyValues.length) {
        return dailyValues.reduce((total, day) => {
          const value = Number(day?.loginsUniq ?? day?.logins ?? 0)
          return total + (Number.isFinite(value) ? value : 0)
        }, 0)
      }
    }

    const dataset = this.chartLoginsData?.datasets?.[0]?.data
    if (Array.isArray(dataset) && dataset.length) {
      return dataset.reduce((total, value) => {
        const numeric = Number(value)
        return total + (Number.isFinite(numeric) ? numeric : 0)
      }, 0)
    }

    return 0
  },
  newsPublishedDuringPeriod () {
    if (typeof this.newsPublishedCount === 'number' && Number.isFinite(this.newsPublishedCount)) {
      return this.newsPublishedCount
    }
    return 0
  },
  newsPublishedTotal () {
    if (typeof this.newsPublishedTotalCount === 'number' && Number.isFinite(this.newsPublishedTotalCount)) {
      return this.newsPublishedTotalCount
    }
    return this.newsPublishedDuringPeriod
  },
  newsViewsTotal () {
    const total = this.counters?.total?.newsViews
    if (Number.isFinite(total)) return total
    if (Array.isArray(this.topNewsItems)) {
      return this.topNewsItems.reduce((sum, a) => sum + (Number(a?.views) || 0), 0)
    }
    return 0
  },
  newsViewsPeriod () {
    const period = this.counters?.period?.newsViews
    if (Number.isFinite(period)) return period
    return 0
  },
  activeSurveys () {
    const now = new Date()
    const groups = Array.isArray(this.activeSurveysRaw) ? this.activeSurveysRaw : []
    const normalized = groups
      .map(group => this.normalizeSurveyGroup(group))
      .filter(Boolean)
      .filter(item => this.isSurveyActive(item, now) && !item.isSingle)

    return normalized.sort((a, b) => {
      if (a.startAt && b.startAt) {
        return a.startAt.getTime() - b.startAt.getTime()
      }
      if (a.startAt) {
        return -1
      }
      if (b.startAt) {
        return 1
      }
      if (a.endAt && b.endAt) {
        return a.endAt.getTime() - b.endAt.getTime()
      }
      return 0
    })
  },
  activeSurveysCount () {
    const metaValue = Number(this.activeSurveysTotal ?? this.surveysOverviewMeta?.activeTotal)
    if (Number.isFinite(metaValue) && metaValue >= 0) {
      return metaValue
    }
    return Array.isArray(this.activeSurveysRaw) ? this.activeSurveysRaw.length : 0
  },
  lastEndedSurvey () {
    if (!this.lastEndedSurveyRaw) {
      return null
    }

    const normalized = this.normalizeSurveyGroup(this.lastEndedSurveyRaw)
    return normalized || null
  },
  pendingNews () {
    return (this.pendingNewsRaw || [])
      .filter(item => {
        return Boolean(item && item.uuid)
      })
      .sort((a, b) => {
        const rawOrderA = a?._fetchedOrder
        const rawOrderB = b?._fetchedOrder
        const orderA = Number(rawOrderA)
        const orderB = Number(rawOrderB)
        const hasOrderA = rawOrderA !== undefined && rawOrderA !== null && Number.isFinite(orderA)
        const hasOrderB = rawOrderB !== undefined && rawOrderB !== null && Number.isFinite(orderB)

        if (hasOrderA && hasOrderB) {
          return orderA - orderB
        }
        if (hasOrderA) {
          return -1
        }
        if (hasOrderB) {
          return 1
        }

        const timeA = new Date(a.publication_date).getTime()
        const timeB = new Date(b.publication_date).getTime()

        const validA = Number.isFinite(timeA)
        const validB = Number.isFinite(timeB)

        if (!validA && !validB) {
          return 0
        }
        if (!validA) {
          return 1
        }
        if (!validB) {
          return -1
        }

        return timeA - timeB
      })
  },
  mavieproNews () {
    return (this.mavieproNewsRaw || [])
      .map(item => {
        const publicationDate = item?.publication_date || item?.send_notification_at || item?.created_at || null
        const updatedAt = item?.updated_at || publicationDate
        const title = item?.title || 'Actualité Maviepro'
        const uuid = item?.uuid || null
        const isEnabled = typeof item?.is_enabled === 'boolean' ? item.is_enabled : false

        return {
          uuid,
          title,
          publication_date: publicationDate,
          updatedAt,
          isEnabled
        }
      })
      .filter(item => {
        if (!item || !item.uuid || !item.updatedAt) {
          return false
        }
        const updatedAt = new Date(item.updatedAt)
        return !Number.isNaN(updatedAt.getTime())
      })
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt)
        const dateB = new Date(b.updatedAt)
        return dateB.getTime() - dateA.getTime()
      })
      .slice(0, 3)
  },
  pendingSurveys () {
    return (this.pendingSurveysRaw || [])
      .map(item => {
        const surveysArray = Array.isArray(item?.surveys) ? item.surveys : []
        const firstSurvey = surveysArray.length ? surveysArray[0] : null
        const publicationDate = item?.publication_date || firstSurvey?.publication_date || item?.start_at || firstSurvey?.start_at || null
        const title = item?.title || item?.group_title || firstSurvey?.question || firstSurvey?.title || 'Sondage'
        const groupUuid = item?.uuid || null
        const surveyUuid = firstSurvey?.uuid || groupUuid
        const isEnabled = typeof item?.is_enabled === 'boolean' ? item.is_enabled : typeof firstSurvey?.is_enabled === 'boolean' ? firstSurvey.is_enabled : false

        return {
          uuid: groupUuid || surveyUuid,
          groupUuid: groupUuid || null,
          surveyUuid,
          title,
          publication_date: publicationDate,
          isEnabled
        }
      })
      .filter(item => {
        if (!item || item.isEnabled) {
          return false
        }
        if (!item.publication_date || !item.uuid || !item.surveyUuid) {
          return false
        }
        const publicationDate = new Date(item.publication_date)
        return !Number.isNaN(publicationDate.getTime())
      })
      .sort((a, b) => {
        const dateA = new Date(a.publication_date)
        const dateB = new Date(b.publication_date)
        return dateB.getTime() - dateA.getTime()
      })
      .slice(0, 3)
  },
  totalPendingNewsCount () {
    const pendingCount = Array.isArray(this.pendingNews) ? this.pendingNews.length : 0
    const mavieproCount = Array.isArray(this.mavieproNews) ? this.mavieproNews.length : 0
    return pendingCount + mavieproCount
  },
  mavieproSurveys () {
    const domainUuid = this.domain?.uuid || null
    const normalized = normalizeMavieproSurveysList(this.mavieproSurveysRaw, domainUuid)
    return normalized.slice(0, 3)
  },
  totalPendingSurveysCount () {
    const metaPending = Number(this.pendingSurveysTotal ?? this.surveysOverviewMeta?.pendingTotal)
    const pendingCount = Number.isFinite(metaPending) ? metaPending : (Array.isArray(this.pendingSurveysRaw) ? this.pendingSurveysRaw.length : 0)
    const domainUuid = this.domain?.uuid || null
    const mavieproCount = normalizeMavieproSurveysList(this.mavieproSurveysRaw, domainUuid).length
    return pendingCount + mavieproCount
  }
}
